import React, { useState, useEffect, useMemo } from "react";
import "./HomePage.scss";
import axios from "axios";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";

const CustomerTestimonials: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [slidesToShow, setSlidesToShow] = useState(4);
  const [feedbackData, setFeedbackData] = useState<any[]>([]);

  const token = localStorage.getItem("customer-token");
  const fetchingTenant = window.location.hostname.split(".")[0];
  const apiPort = process.env.REACT_APP_API_PROTOCOL;

  const fetchVehicle = async () => {
    try {
      const response = await axios.get(
        `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/gc/featured-feedback/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFeedbackData(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching feedback:", error);
    }
  };

  useEffect(() => {
    fetchVehicle();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width <= 480) {
        setSlidesToShow(1);
      } else if (width <= 768) {
        setSlidesToShow(2);
      } else if (width <= 1024) {
        setSlidesToShow(3);
      } else {
        setSlidesToShow(4);
      }
      setCurrentPage(0); // reset to first page when screen size changes
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Function to chunk data into pages
  const chunkArray = (array: any[], size: number) => {
    const result = [];
    for (let i = 0; i < array.length; i += size) {
      result.push(array.slice(i, i + size));
    }
    return result;
  };

  // Recalculate pages whenever data or slidesToShow changes
  const pages = useMemo(() => {
    return chunkArray(feedbackData, slidesToShow);
  }, [feedbackData, slidesToShow]);

  const totalPages = pages.length;
  const maxPageIndex = totalPages - 1;

  const nextPage = () => {
    setCurrentPage((prev) => (prev < maxPageIndex ? prev + 1 : 0));
  };

  const prevPage = () => {
    setCurrentPage((prev) => (prev > 0 ? prev - 1 : maxPageIndex));
  };

  const goToPage = (pageIndex: number) => {
    setCurrentPage(Math.max(0, Math.min(pageIndex, maxPageIndex)));
  };

  // Auto-play
  useEffect(() => {
    if (isAutoPlaying && totalPages > 1) {
      const interval = setInterval(() => {
        nextPage();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [isAutoPlaying, totalPages]);

  const handleMouseEnter = () => setIsAutoPlaying(false);
  const handleMouseLeave = () => setIsAutoPlaying(true);

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <span key={index} className={`star ${index < rating ? "filled" : ""}`}>
        ★
      </span>
    ));
  };

  return (
    <div className="testimonials-container">
      <div className="testimonials-header">
        <h2>Customer Stories That Drive Us</h2>
        <div className="header-underline"></div>
      </div>

      <div
        className="testimonials-slider"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button
          className="nav-button nav-button--prev"
          onClick={prevPage}
          aria-label="Previous testimonials"
          disabled={currentPage === 0}
        >
          ‹
        </button>

        <div className="testimonials-track">
          <div
            className="testimonials-slides"
            style={{
              transform: `translateX(-${currentPage * (100 / totalPages)}%)`,
              width: `${totalPages * 100}%`,
              display: "flex",
              transition: "transform 0.5s ease-in-out",
            }}
          >
            {pages.length > 0 ? (
              pages.map((page, pageIndex) => (
                <div
                  key={pageIndex}
                  className="testimonial-page"
                  style={{
                    width: `${100 / totalPages}%`,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  {page.map((testimonial) => (
                    <div
                      key={testimonial.id}
                      className="testimonial-slide"
                      style={{
                        width: `${100 / slidesToShow}%`,
                        flexShrink: 0,
                      }}
                    >
                      <div className="testimonial-card">
                        <div className="quote-content">
                          <FormatQuoteIcon
                            sx={{
                              color: "#f4c430",
                              fontSize: 18,
                              marginRight: "4px",
                            }}
                          />
                          <p>{testimonial.feedback}</p>
                          <FormatQuoteIcon
                            sx={{
                              color: "#f4c430",
                              fontSize: 18,
                              marginLeft: "4px",
                            }}
                          />
                        </div>
                        <div className="testimonial-footer">
                          <div className="rating">
                            {renderStars(testimonial?.rating)}
                          </div>
                          <div className="author">
                            {testimonial?.customer_detail?.name}
                          </div>
                          {/* <div className="testimonial-workshop">
                            {testimonial?.workshop_detail?.name}
                          </div> */}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <div
                className="testimonial-slide"
                style={{ width: "100%", textAlign: "center" }}
              >
                No testimonials available.
              </div>
            )}
          </div>
        </div>

        <button
          className="nav-button nav-button--next"
          onClick={nextPage}
          aria-label="Next testimonials"
          disabled={currentPage === maxPageIndex}
        >
          ›
        </button>
      </div>

      {totalPages > 1 && (
        <div className="testimonials-indicators">
          {Array.from({ length: totalPages }, (_, index) => (
            <button
              key={index}
              className={`indicator ${index === currentPage ? "active" : ""}`}
              onClick={() => goToPage(index)}
              aria-label={`Go to page ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomerTestimonials;
